import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

export type StepStatus = 'pending' | 'in-progress' | 'completed' | 'failed';

export interface ProgressStep {
  label: string;
  status: StepStatus;
  errorMessage?: string;
  errorDetail?: string;
  correlationIdentifier?: string;
}

function FailedStepDetails({ step }: { step: ProgressStep }) {
  return (
    <>
      {step.errorMessage && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {step.errorMessage}
        </Typography>
      )}
      {step.errorDetail && (
        <Typography variant="body2" color="error" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
          {step.errorDetail}
        </Typography>
      )}
      {step.correlationIdentifier && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Brug venligst følgende ID ved supporthenvendelser: <strong>{step.correlationIdentifier}</strong>
        </Typography>
      )}
    </>
  );
}

interface SpAssignmentProgressModalProps {
  open: boolean;
  steps: ProgressStep[];
  onRetry: () => void;
  onClose: () => void;
  onGoToReceive: () => void;
}

function StepIcon({ status }: { status: StepStatus }) {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon color="success" />;
    case 'in-progress':
      return <CircularProgress size={24} />;
    case 'failed':
      return <ErrorIcon color="error" />;
    default:
      return <RadioButtonUncheckedIcon color="disabled" />;
  }
}

export function SpAssignmentProgressModal({ open, steps, onRetry, onClose, onGoToReceive }: SpAssignmentProgressModalProps) {
  const failedStep = steps.find(s => s.status === 'failed');
  const hasFailed = Boolean(failedStep);
  const isRunning = steps.some(s => s.status === 'in-progress');
  const isCompleted = steps.every(s => s.status === 'completed');

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown={isRunning}>
      <DialogTitle>Opretter bestilling</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          {steps.map((step, index) => (
            <React.Fragment key={step.label}>
              <StepIcon status={step.status} />
              {index < steps.length - 1 && <Box sx={{ width: 32, height: 2, bgcolor: 'divider' }} />}
            </React.Fragment>
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" align="center">
          {isCompleted ? 'Bestilling oprettet' : 'Opretter bestilling...'}
        </Typography>
        {failedStep && <FailedStepDetails step={failedStep} />}
      </DialogContent>
      <DialogActions>
        {isCompleted && (
          <Button onClick={onGoToReceive} variant="contained" color="primary">
            Gå til modtagelse
          </Button>
        )}
        {hasFailed && (
          <Button onClick={onRetry} variant="contained" color="primary">
            Prøv igen
          </Button>
        )}
        {!isCompleted && (
          <Button onClick={onClose} disabled={isRunning}>
            Luk
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
